import React, { useState, FocusEvent } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { ThemeProvider } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { customDatePickerTheme } from './theme'
import { muiOptions, MuiProps } from '../../../../infrastructure/materialUiThemeProvider'
import moment, { Moment } from 'moment'
import { t } from '../../../../infrastructure/i18nextHelper'
import { BaseFieldProps } from '../../fieldProps'
import { fieldStatuses } from '../../styles'
import { useDatePickerState } from './state'
import { FieldStatus } from '../../fieldsStatus'

type DatePickerStyleProps = {
    datepicker?
}

type DatePickerProps = {
    date: string | null
    setDate: (newDate: string | null) => void
    auto?: boolean
    errormessage?: string
    maxDate?: string
    label: string
    placeholder?: string
    disableFuture?: boolean
    disableNewStyle?: boolean
    hideUnderline?: boolean
    smallIcon?: boolean
    onLostFocus?: () => void
    classesOverride?: DatePickerStyleProps
    size?: 'small' | 'standard'
} & BaseFieldProps & MuiProps

function _DatePicker({
    classes, classesOverride, date, placeholder, onLostFocus, setDate, disableFuture, auto, status, onError, disableNewStyle,
    hideUnderline, smallIcon, ...props }: DatePickerProps) {
    let state = useDatePickerState(date, setDate, onError)

    let highlight = status === 'info' ? classes.fieldStatusInfo
        : status === 'warning' ? classes.fieldStatusWarning : ''

    let sizeClass = props.size === 'standard' ? classes.standard : props.size === 'small' ? classes.small : ''

    return (
        <ThemeProvider theme={customDatePickerTheme}>
            <DatePicker
                value={date ? moment(date) : null}
                onChange={(newValue: Moment | null) => state.onChange(newValue)}
                disableFuture={!!disableFuture}
                maxDate={props.maxDate ? moment(props.maxDate) : undefined}
                format='MM/DD/YYYY'
                closeOnSelect={false}
                slotProps={{
                    textField: {
                        className: `${classesOverride ? classesOverride.datepicker : classes.picker} ${highlight} ${sizeClass}`,
                        error: status === 'alert' || state.isOnError,
                        label: props.label,
                        placeholder: props.disabled
                            ? ''
                            : (placeholder ? moment(placeholder) : moment()).format('MM/DD/YYYY'),
                        InputLabelProps: { shrink: true },
                        InputProps: {
                            disableUnderline: hideUnderline || props.disabled || false,
                        },
                        helperText:
                            status === 'alert' || onError
                                ? (props.errormessage ? props.errormessage : t('components.error'))
                                : (auto ? t('components.autoCalculated') : null),
                        onBlur: onLostFocus,
                        size: props.size === 'small' ? 'small' : 'medium',
                        disabled: props.disabled,
                        variant: !disableNewStyle ? 'filled' : 'standard'
                    },
                    openPickerButton: { size: smallIcon ? 'small' : 'medium' },
                    actionBar: { actions: ['clear', 'cancel', 'accept'], },
                    toolbar: {
                        hidden: false,
                    },
                }}
                {...props}
            />
        </ThemeProvider>)
}

type DatePeriod = {
    startDate: string | null
    endDate: string | null
}

type DateRangePickerProps = {
    startDate: string | null
    endDate: string | null
    onPeriodChange: (period: DatePeriod) => void
    startLabel: string
    endLabel: string
    classesOverride?: DatePickerStyleProps
    size?: 'small' | 'standard'
    startAuto?: boolean
    endAuto?: boolean
    startDisabled?: boolean
    endDisabled?: boolean
    startStatus?: FieldStatus
    endStatus?: FieldStatus
    disableNewStyle?: boolean
    autoReplace?: boolean
} & BaseFieldProps & MuiProps

let _DateRangePicker = ({ classes, classesOverride, startDate, endDate, onPeriodChange, startLabel, endLabel, size, ...props }: DateRangePickerProps) => {
    let handleStartDateChange = (date: string | null) => {
        if (!endDate || (endDate && moment(date).isAfter(moment(endDate)) && props.autoReplace))
            onPeriodChange({ startDate: date, endDate: date })
        else
            onPeriodChange({ startDate: date, endDate: endDate })
    }

    let handleEndDateChange = (date: string | null) => {
        if (!startDate || (startDate && moment(date).isBefore(startDate)) && props.autoReplace)
            onPeriodChange({ startDate: date, endDate: date })
        else
            onPeriodChange({ startDate: startDate, endDate: date })
    }

    return (
        <>
            <CustomDatePicker
                key={startLabel}
                label={startLabel}
                classes={classes}
                classesOverride={classesOverride}
                date={startDate}
                setDate={handleStartDateChange}
                disabled={props.disabled || props.startDisabled}
                disableNewStyle={props.disableNewStyle}
                onError={props.onError}
                status={props.status || props.startStatus}
                size={size}
                auto={props.startAuto}
            />
            <CustomDatePicker
                key={endLabel}
                label={endLabel}
                classes={classes}
                classesOverride={classesOverride}
                date={endDate}
                setDate={handleEndDateChange}
                disabled={props.disabled || props.endDisabled}
                disableNewStyle={props.disableNewStyle}
                onError={props.onError}
                status={props.status || props.endStatus}
                size={size}
                auto={props.endAuto}
            />
        </>
    )
}

type MonthPickerProps = {
    date: string | null
    setDate: (newDate: string | null) => void
    auto?: boolean
    label?: string
    disablePast?: boolean
    disableNewStyle?: boolean
    onLostFocus?: () => void
    size?: 'small' | 'standard'
    classesOverride?: DatePickerStyleProps
} & BaseFieldProps & MuiProps

let _MonthPicker = ({ classes, classesOverride, date, onLostFocus, setDate, disablePast, auto, onError, disableNewStyle, ...props }: MonthPickerProps) => {
    let state = useDatePickerState(date, setDate, onError)

    let highlight = props.status === 'info' ? classes.fieldStatusInfo
        : props.status === 'warning' ? classes.fieldStatusWarning : ''

    let sizeClass = props.size === 'standard' ? classes.standard : props.size === 'small' ? classes.small : ''

    return (
        <ThemeProvider theme={customDatePickerTheme}>
            <DatePicker
                value={date ? moment(date) : null}
                onChange={(newDate: Moment | null) => {
                    if (newDate?.isValid()) {
                        newDate = newDate.startOf('month')
                        state.onChange(newDate)
                    } else if (!newDate) {
                        state.onChange(null)
                    }
                }}
                views={['year', 'month']}
                openTo="month"
                disablePast={!!disablePast}
                format="MM/YYYY"
                closeOnSelect={false}
                slotProps={{
                    textField: {
                        className: `${classesOverride ? classesOverride.datepicker : classes.picker} ${highlight} ${sizeClass}`,
                        error: props.status === 'alert' || state.isOnError,
                        label: props.label,
                        placeholder: moment().format('MM/YYYY'),
                        InputLabelProps: { shrink: true },
                        InputProps: {
                            disableUnderline: props.disabled,
                        },
                        helperText: auto ? t('components.autoCalculated') : null,
                        onBlur: onLostFocus,
                        size: props.size === 'small' ? 'small' : 'medium',
                        disabled: props.disabled,
                        variant: !disableNewStyle ? 'filled' : 'standard'
                    },
                    actionBar: { actions: ['clear', 'cancel', 'accept'], },
                    toolbar: {
                        hidden: false,
                    }
                }}
                disabled={props.disabled}
                {...props}
            />
        </ThemeProvider>)
}

type MonthRangePickerProps = {
    classes: any
    classesOverride?: any
    startDate: string | null
    endDate: string | null
    onLostFocus?: (event: FocusEvent<HTMLInputElement>) => void
    onStartDateChange: (newDate: string | null) => void
    onEndDateChange: (newDate: string | null) => void
    disablePast?: boolean
    auto?: boolean
    onError?: boolean
    disableNewStyle?: boolean
    label?: string
    status?: 'info' | 'warning' | 'alert'
    size?: 'standard' | 'small'
}

let _MonthRangePicker = ({ classes, classesOverride, startDate, endDate, onStartDateChange, onEndDateChange, disablePast, auto, onError, disableNewStyle, status, size, label }: MonthRangePickerProps) => {
    let [newStartDate, setNewStartDate] = useState<string | null>(startDate)
    let [newEndDate, setNewEndDate] = useState<string | null>(endDate)
    let handleStartDateChange = (date) => {
        if (!newEndDate || (newEndDate && date > newEndDate)) {
            setNewEndDate(date)
            onEndDateChange(date)
        }
        setNewStartDate(date)
        onStartDateChange(date)
    }

    let handleEndDateChange = (date) => {
        if (!newStartDate || (newStartDate && date < newStartDate)) {
            setNewStartDate(date)
            onStartDateChange(date)
        }
        setNewEndDate(date)
        onEndDateChange(date)
    }

    return (
        <div>
            <div className='container'>
                <label className={classes.label}>{label}</label>
            </div>
            <MonthPicker
                classesOverride={classesOverride}
                date={newStartDate}
                setDate={handleStartDateChange}
                disablePast={disablePast}
                auto={auto}
                onError={onError}
                disableNewStyle={disableNewStyle}
                status={status}
                size={size}
            />
            <MonthPicker
                classesOverride={classesOverride}
                date={newEndDate}
                setDate={handleEndDateChange}
                disablePast={disablePast}
                auto={auto}
                onError={onError}
                disableNewStyle={disableNewStyle}
                status={status}
                size={size}
            />
        </div>
    )
}

let styles = _ =>
    createStyles({
        small: { width: '8em !important' },
        standard: { width: '10.3em !important' },
        picker: {
            width: '15.3em',
            margin: '0em 0.5em',
            "& .Mui-disabled": {
                backgroundColor: 'white',
                "& span": {
                    color: 'white'
                }
            }
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1em'
        },
        label: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '12px',
            fontFamily: 'Arial, sans-serif',
            padding: '0.5em'
        },
        ...fieldStatuses
    })

export let CustomDatePicker = withStyles(styles, muiOptions)(_DatePicker)
export let DateRangePicker = withStyles(styles, muiOptions)(_DateRangePicker)
export let MonthPicker = withStyles(styles, muiOptions)(_MonthPicker)
export let MonthRangePicker = withStyles(styles, muiOptions)(_MonthRangePicker)
